import React, { useState } from "react";
import clsx from "clsx";
import InfoTooltip from "../InfoTooltip";

type PricingCardProps = {
  plan?: string;
  price?: string;
  licenses: number[];
  credits: number[];
  featureState?: { [key: string]: boolean };
  aiInsights?: boolean;
  popular?: boolean;
  mostPopular?: boolean;
  current?: boolean;
  description?: string;
};

const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  price,
  licenses,
  credits,
  featureState,
  popular,
  mostPopular,
  current,
  description,
}) => {
  const [selectedLicenses] = useState<number[]>([...licenses]);
  const [selectedCredits] = useState<number[]>([...credits]);
  const allFeatures = Object.keys(featureState || {});
  const [selectedFeatures] = useState<string[]>(
    allFeatures.filter((f) => featureState?.[f]),
  );

  return (
    <div
      className={clsx(
        "w-full max-w-xs shadow-lg rounded-xl transition-transform duration-300 hover:scale-[1.02] text-white relative cursor-pointer",
        !popular ? "bg-[#1A1A1A] border border-gray-700 relative pricing-card-normal" : "pricing-card-popular",
      )}
      style={
        popular
          ? {
              background:
                "linear-gradient(82.52deg, #501B99 1.75%, #1B0933 97.03%)",
            }
          : {}
      }
    >
      {mostPopular && (
        <span className="absolute top-2 right-2 text-xs text-white font-semibold poppins px-3 py-0.5 bg-[#6D28D9] rounded-full">
          Most Popular
        </span>
      )}

      {current && (
        <span className="absolute top-2 right-2 text-xs text-green-400 font-semibold poppins px-3 py-0.5 bg-green-900 rounded-full">
          Current Plan
        </span>
      )}

      <div className="flex flex-col justify-between h-full p-4">
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-3xl font-semibold">{plan}</h2>
          </div>

          <div className="flex mb-4">
            <p className="text-3xl font-bold">{price}</p>
            <span className="ml-2 text-[8px] font-light mt-1.5">
              {description}
            </span>
          </div>

          <div className="mt-4">
            <div className="flex items-center gap-2">
              <p className="font-normal underline underline-offset-4 text-gray-300 poppins">
                Licenses
              </p>
              <InfoTooltip message="Select the types of licenses you need" />
            </div>

            <div className="flex flex-wrap justify-between w-60 mt-2">
              {licenses.map((l) => (
                <label
                  key={l}
                  className="flex items-center gap-2 text-sm px-2 font-semibold py-1 text-white cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="form-checkbox accent-[#B8F890] w-4 h-4"
                    checked={selectedLicenses.includes(l)}
                  />
                  {l}
                </label>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center gap-2">
              <p className="font-normal underline underline-offset-4 text-gray-300 poppins">
                Token Credits
              </p>
              <InfoTooltip message="Select the types of licenses you need" />
            </div>
            <div className="flex flex-wrap justify-between w-60">
              {credits.map((c) => (
                <label
                  key={c}
                  className="flex items-center gap-2 text-sm px-2 font-semibold py-1 text-white cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="form-checkbox accent-[#B8F890] w-4 h-4"
                    checked={selectedCredits.includes(c)}
                  />
                  {c}
                </label>
              ))}
            </div>
          </div>

          <ul className="mt-4 space-y-2 text-sm">
            <p className="font-normal underline underline-offset-4 mb-4 text-gray-300 poppins">
              Features
            </p>
            {allFeatures.map((f, i) => (
              <li key={i} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="form-checkbox accent-[#B8F890] w-4 h-4"
                  checked={selectedFeatures.includes(f)}
                />
                {f}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PricingCard;
